import React, { useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { getBlogDetails, getBlogs } from "../../api/services";
import {
  Box,
  Typography,
  Button,
  Container,
  useMediaQuery,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import CallIcon from '@mui/icons-material/Call';
import { styled } from "@mui/material/styles";
import { Helmet } from 'react-helmet-async';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";


const IMAGE_DOMAIN = "https://dprstorage.b-cdn.net";



const ButtonContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  right: "27px",
  top: "30%",
  transform: "translateY(-50%) rotate(-90deg)",
  transformOrigin: "right center",
  zIndex: 1000,

  [theme.breakpoints.down("sm")]: {
    right: "20px",
    top: "30%",
  },
}));


const AnimatedButton = styled(Button)({
  bgcolor: "#9f8054",
  color: "#fff",
  fontWeight: "bold",
  fontSize: "14px",
  letterSpacing:'1px',
  padding: "15px 20px",
  borderRadius: "10px 10px 0px 0px",
  boxShadow: "0px 4px 15px rgba(159, 128, 84, 0.5)",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  zIndex: 100000,
  border: "1.5px solid #ffffff",
  transition: "transform 0.3s ease, box-shadow 0.3s",
  "&:hover": {
    bgcolor: "#7f6540",
    transform: "scale(1.05)",
    boxShadow: "0px 8px 25px rgba(159, 128, 84, 0.8)",
  },
  animation: "pulse 2s infinite", 
  "@keyframes pulse": {
    "0%": { boxShadow: "0 0 10px rgba(159, 128, 84, 0.5)" },
    "50%": { boxShadow: "0 0 25px rgba(159, 128, 84, 0.8)" },
    "100%": { boxShadow: "0 0 10px rgba(159, 128, 84, 0.5)" },
  },
});


gsap.registerPlugin(ScrollTrigger);

const BlogDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isMobile = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
    const buttonRef = useRef<HTMLButtonElement | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  

  // Fetch blog details by ID/slug
  const { data: blog, isLoading, isError, error } = useQuery(
    ["blog-details", id],
    () => getBlogDetails(id || ""),
    { enabled: !!id }
  );

  // Fetch all blogs for sidebar
  const { data: blogsData } = useQuery(["blogs"], getBlogs);

  // Prepare sidebar blogs: random, exclude current, max 5
  const sidebarBlogs = React.useMemo(() => {
    if (!blogsData?.blogs || !blog) return [];
    const filtered = blogsData.blogs.filter((b: any) => b.slug !== id);
    // Shuffle array for random order
    for (let i = filtered.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
    }
    return filtered.slice(0, 5);
  }, [blogsData, id, blog]);

  const goldGradient =
    "linear-gradient(90deg, #bf742c, rgb(252, 243, 202), #c58038)";

  useEffect(() => {
    const el = sectionRef.current;
    if (el) {
      gsap.fromTo(
        el,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 87%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  if (isLoading)
    return (
      <Box textAlign="center" mt={8}>
        <CircularProgress />
      </Box>
    );

  if (isError)
    return (
      <Typography
        color="error"
        sx={{ textAlign: "center", mt: 8, fontWeight: "bold" }}
      >
        Error: {(error as Error)?.message}
      </Typography>
    );

  if (!blog)
    return (
      <Typography
        color="#ccc"
        sx={{ textAlign: "center", mt: 8, fontWeight: "bold" }}
      >
        Blog not found.
      </Typography>
    );

  // Compose full image URL
  const mainImageUrl = blog.imageurl?.startsWith("http")
    ? blog.imageurl
    : IMAGE_DOMAIN + blog.imageurl;

  return (
    <Box ref={sectionRef} sx={{ backgroundColor: "#181818", minHeight: "100vh", py: 8, position: "relative" }}>
      {/* SEO Helmet */}
      <Helmet>
        <title>{blog.metaTitle || blog.title}</title>
        <meta name="description" content={blog.metaDescription || ""} />
      </Helmet>
      {/* Fixed Contact Button (right side, 35% from top) */}
      {!isMobile ? 
        <ButtonContainer>
          <AnimatedButton
            ref={buttonRef}
            variant="contained"
            onClick={() => navigate("/contactus")}                  >
            <CallIcon />
            Book a Site Visit
          </AnimatedButton>
        </ButtonContainer>
        :  <></> 
      }

      <Container maxWidth="lg">
        {/* Title outside image */}
        

        {/* Hero Image with no edge cutting, always visible */}
        <Box
          sx={{
            width: "100%",
            aspectRatio: "16/7",
            maxHeight: { xs: 260, md: 420 },
            mb: 6,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#222",
          }}
        >
          <Box
            component="img"
            src={mainImageUrl}
            alt={blog.title}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center",
              display: "block",
              borderRadius: 3,
              background: "#222",
            }}
          />
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 500,
            mb: 2,
            fontFamily: "LEMON MILK",
            background: goldGradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            userSelect: "none",
            fontSize: { xs: 22, md: 32 },
            textAlign: "left",
            letterSpacing: 0.5,
          }}
        >
          {blog.title}
        </Typography>

        {/* Two column layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 3 : 6,
          }}
        >
          {/* Left column: Blog content with sections */}
          <Box sx={{ flex: 3 }}>
            {/* Main description */}
            <Typography
              variant="body1"
              sx={{
                color: "#ccc",
                fontSize: { xs: 14, md: 16 },
                mb: 4,
                whiteSpace: "pre-line",
                lineHeight: 1.7,
              }}
            >
              {blog.metaDescription || blog.description || ""}
            </Typography>

            {/* Dynamic sections rendering */}
            {Array.isArray(blog.sections) &&
              blog.sections.map((section: any) => (
                <Box key={section._id} sx={{ mb: 4 }}>
                  <Typography
                    variant={section.type === "h2" ? "h5" : "h6"}
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontFamily: "LEMON MILK",
                      background: goldGradient,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      userSelect: "none",
                      fontSize: { xs: 18, md: 24 },
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#ccc",
                      fontSize: { xs: 14, md: 16 },
                      whiteSpace: "pre-line",
                      lineHeight: 1.7,
                    }}
                  >
                    {section.description}
                  </Typography>
                </Box>
              ))}

            {/* Book a Site Visit/Connect With Us Button */}
            <Box sx={{ mt: 6, textAlign: "center" }}>
              <Button
                variant="contained"
                sx={{
                  background: "linear-gradient(90deg, #bf742c, #c58038)",
                  color: "#fff",
                  fontWeight: 700,
                  borderRadius: "24px",
                  px: 4,
                  py: 1.5,
                  textTransform: "capitalize",
                  fontSize: "18px",
                  letterSpacing: 1,
                  boxShadow: "0 4px 16px rgba(159,128,84,0.18)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #c58038, #bf742c)",
                    color: "#fff",
                  },
                }}
                onClick={() => navigate("/contactus")}
              >
                Book a Site Visit Now
              </Button>
            </Box>
          </Box>

          {/* Right column: Sidebar recent blogs (sticky, no scroll) */}
          <Box
            sx={{
              flex: 1,
              border: "1px solid #444",
              borderRadius: 3,
              p: 2,
              backgroundColor: "#222",
              position: isMobile ? "static" : "sticky",
              top: isMobile ? undefined : 100,
              alignSelf: "flex-start",
              minWidth: isMobile ? "100%" : 260,
              maxWidth: isMobile ? "100%" : 340,
              mb: isMobile ? 4 : 0,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: "#c7a86b",
                mb: 3,
                userSelect: "none",
                fontSize: 14,
              }}
            >
              Also Check Out Other Blogs To Know More
            </Typography>

            <List sx={{ p: 0, m: 0 }}>
              {sidebarBlogs.map((b: any) => {
                const sidebarImageUrl = b.imageurl?.startsWith("http")
                  ? b.imageurl
                  : IMAGE_DOMAIN + b.imageurl;

                return (
                  <ListItem
                    key={b._id}
                    sx={{
                      p: 1,
                      mb: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "rgba(191, 116, 44, 0.1)" },
                      flexDirection: isMobile ? "row" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                    }}
                    onClick={() => navigate(`/blogs/${b.slug}`)}
                  >
                    <ListItemAvatar>
                      <Avatar
                        variant="rounded"
                        src={sidebarImageUrl}
                        alt={b.title}
                        sx={{
                          width: isMobile ? 60 : 100,
                          height: isMobile ? 45 : 70,
                          borderRadius: 2,
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#ccc",
                            fontWeight: 600,
                            fontSize: isMobile ? 13 : 14,
                            lineHeight: 1.2,
                          }}
                        >
                          {b.title}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#999",
                            display: isMobile ? "none" : "block",
                          }}
                        >
                          {/* optional author or date here */}
                        </Typography>
                      }
                      sx={{ ml: 1 }}
                    />
                  </ListItem>
                );
              })}
            </List>

            {/* View More Button */}
            <Box sx={{ textAlign: "right", mt: 1 }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: "#c7a86b",
                  color: "#c7a86b",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 16,
                  px: 2,
                  py: 0.5,
                  fontSize: 13,
                  letterSpacing: 0.5,
                  "&:hover": {
                    backgroundColor: "#c7a86b",
                    color: "#181818",
                    borderColor: "#c7a86b",
                  },
                }}
                onClick={() => navigate("/blogs")}
              >
                View More Blog
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default BlogDetails;
